// Iter-f5 — Per-branch Cancellation drill-down, dual-period edition.
//
// Opened from a branch card on /staff/brand/cancellations. Every chart
// now plots Period A alongside Period B — grouped bars for the daily
// and hour-of-day series, primary A bar + muted B comparison marker
// for all top-N bars, and primary A pie / donut with a Period B
// reference-count rendered inside each slice label.
//
// No engine files are imported or edited.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, ArrowUpRight, ArrowDownRight, Minus, Download } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell, LabelList,
} from "recharts";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/AuthEngine";
import { todayBali } from "@/lib/ReportingEngine";
import { useDashboardSnapshot, buildRangeSnapshotFilename } from "@/lib/NotificationEngine";
import { InfoHint } from "@/components/common/InfoHint";
import { CANCELLATION_TIPS } from "@/lib/reportTips";

// Palette.
const COLOR_A           = "#2563eb";
const COLOR_A_SECONDARY = "#60a5fa";
const COLOR_B           = "#94a3b8";
const COLOR_B_SECONDARY = "#cbd5e1";
const COLOR_CANCELLED   = "#2563eb";
const COLOR_NO_SHOW     = "#dc2626";

const PALETTE_PIE = [
  "#2563eb", "#7c3aed", "#db2777", "#dc2626", "#ea580c",
  "#ca8a04", "#16a34a", "#0d9488", "#0891b2", "#4338ca",
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatDate(d) {
  if (!d) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  return `${m[3]} ${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
}
function startOfMonth(d) {
  const [y, m] = d.split("-");
  return `${y}-${m}-01`;
}
function sameWindowPriorYear(from, to) {
  const [yf, mf, df] = from.split("-").map(Number);
  const [yt, mt, dt] = to.split("-").map(Number);
  const fy = String(yf - 1).padStart(4, "0");
  const ty = String(yt - 1).padStart(4, "0");
  return [
    `${fy}-${String(mf).padStart(2, "0")}-${String(df).padStart(2, "0")}`,
    `${ty}-${String(mt).padStart(2, "0")}-${String(dt).padStart(2, "0")}`,
  ];
}

function ChartCard({ title, testId, children, height = 340, tooltip }) {
  return (
    <Card className="rounded-lg relative" data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-center mb-3 pr-5">{title}</p>
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Daily chart X-axis tick: show "Day N" plus the A date and B date
// stacked, so operators can still read the actual calendar day on
// either side of the comparison.
function DailyDualTick({ x, y, payload, dataLookup }) {
  const idx = payload?.value;
  const row = dataLookup?.[idx] || {};
  const dayLabel = `Day ${idx || ""}`;
  const fa = row.date_a ? row.date_a.slice(5) : "";
  const fb = row.date_b ? row.date_b.slice(5) : "";
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="middle" fontSize={10} fill="#374151">{dayLabel}</text>
      {fa && <text x={0} y={0} dy={24} textAnchor="middle" fontSize={9} fill="#2563eb">A·{fa}</text>}
      {fb && <text x={0} y={0} dy={36} textAnchor="middle" fontSize={9} fill="#64748b">B·{fb}</text>}
    </g>
  );
}

// Two side-by-side pies — one per period — so Period B is a real chart
// rather than a reference number inside Period A's slice labels.
function DualPieCard({ title, testId, tooltip, labelA, labelB, dataA, dataB, donut = false, height = 280 }) {
  const panels = [
    { key: "a", label: labelA, rows: dataA || [] },
    { key: "b", label: labelB, rows: dataB || [] },
  ];
  return (
    <Card className="rounded-lg relative" data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-center mb-3 pr-5">{title}</p>
        <div className="grid grid-cols-2 gap-2">
          {panels.map((p) => {
            const total = p.rows.reduce((s, r) => s + (r.value || 0), 0);
            return (
              <div key={p.key} data-testid={`${testId}-${p.key}`}>
                <p className="text-[11px] font-medium text-center text-muted-foreground mb-1">{p.label}</p>
                <div style={{ width: "100%", height }}>
                  {total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={p.rows}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={donut ? 40 : 0}
                          outerRadius={62}
                          label={(e) => (e.value > 0 ? `${e.value} (${Math.round((e.value / total) * 100)}%)` : "")}
                          labelLine={false}
                          isAnimationActive={false}
                        >
                          {p.rows.map((e, i) => <Cell key={i} fill={e.fill} />)}
                        </Pie>
                        <ChartTooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                      No data in this period.
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-center text-muted-foreground" data-testid={`${testId}-${p.key}-total`}>
                  Total {total.toLocaleString("en-US")}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function DeltaBadge({ delta, testId }) {
  let Icon = Minus;
  let cls = "text-muted-foreground";
  if (delta != null && delta > 0) { Icon = ArrowUpRight; cls = "text-emerald-600"; }
  if (delta != null && delta < 0) { Icon = ArrowDownRight; cls = "text-rose-600"; }
  const label = delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${cls}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

export default function BrandCancellationDetail() {
  const { bid } = useParams();
  const nav = useNavigate();
  const { staff } = useAuth();
  const [sp] = useSearchParams();
  const today = todayBali();
  const [data, setData] = useState(null);
  const [branchName, setBranchName] = useState("");
  const [loading, setLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);

  // Query-string defaults — dashboard deep-links carry the active A/B.
  const defaultA = useMemo(() => ({
    from: sp.get("a_from") || startOfMonth(today),
    to:   sp.get("a_to")   || today,
  }), [sp, today]);
  const defaultB = useMemo(() => {
    const [pyFrom, pyTo] = sameWindowPriorYear(defaultA.from, defaultA.to);
    return {
      from: sp.get("b_from") || pyFrom,
      to:   sp.get("b_to")   || pyTo,
    };
  }, [sp, defaultA]);

  const [aFrom, setAFrom] = useState(defaultA.from);
  const [aTo,   setATo]   = useState(defaultA.to);
  const [bFrom, setBFrom] = useState(defaultB.from);
  const [bTo,   setBTo]   = useState(defaultB.to);

  const [brandCancellationTick, setBrandCancellationTick] = useState(false);
  useEffect(() => {
    let cancelled = false;
    staffApi.get("/report-permissions/me", { params: { scope: "brand" } })
      .then((r) => {
        if (cancelled) return;
        const keys = new Set(r.data?.allowed_keys || []);
        setBrandCancellationTick(keys.has("cancellation"));
      })
      .catch(() => { /* backend enforces too */ });
    return () => { cancelled = true; };
  }, []);
  const canSeeCancellation = isOwner(staff) || brandCancellationTick;

  useEffect(() => {
    staffApi.get("/branches")
      .then((r) => {
        const b = (r.data || []).find((x) => x.id === bid);
        setBranchName(b?.name || "");
      })
      .catch(() => { /* non-blocking */ });
  }, [bid]);

  const params = useMemo(() => ({
    a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo,
  }), [aFrom, aTo, bFrom, bTo]);

  const load = useCallback(() => {
    if (!canSeeCancellation || !bid) return;
    setLoading(true);
    setAccessError(null);
    staffApi.get(`/reports/brand/cancellations/detail/${bid}`, { params })
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 403) {
          setAccessError("You don't have access to this branch.");
          setData(null);
        } else {
          toast.error(formatApiError(err));
        }
      })
      .finally(() => setLoading(false));
  }, [canSeeCancellation, bid, params]);

  useEffect(() => { load(); }, [load]);

  // --- Derived series ---------------------------------------------------
  const daily = data?.daily || [];
  const dailyLookup = useMemo(() => {
    const map = {};
    daily.forEach((r) => { map[r.day_index] = r; });
    return map;
  }, [daily]);

  const statusA = data?.client_status_split_a || [];
  const statusB = data?.client_status_split_b || [];
  const toStatusRows = (rows) => rows.map((r) => ({
    name: r.status === "cancelled" ? "Cancelled" : "No-show",
    value: r.count,
    fill: r.status === "cancelled" ? COLOR_CANCELLED : COLOR_NO_SHOW,
  }));
  const statusDataA = toStatusRows(statusA);
  const statusDataB = toStatusRows(statusB);

  const weekdayRows = data?.by_weekday || [];
  const weekdayDataA = weekdayRows.map((r, i) => ({
    name: r.weekday,
    value: r.count_a,
    fill: PALETTE_PIE[i % PALETTE_PIE.length],
  }));
  const weekdayDataB = weekdayRows.map((r, i) => ({
    name: r.weekday,
    value: r.count_b,
    fill: PALETTE_PIE[i % PALETTE_PIE.length],
  }));

  const labelA = `Period A · ${formatDate(aFrom)} → ${formatDate(aTo)}`;
  const labelB = `Period B · ${formatDate(bFrom)} → ${formatDate(bTo)}`;
  const { downloadRef, downloadPng, capturing } = useDashboardSnapshot({
    label: `Cancellation ${branchName || ""}`.trim(),
    dateFrom: aFrom,
    dateTo: aTo,
    filename: buildRangeSnapshotFilename(
      `Cancellation ${branchName || ""}`.trim(), aFrom, aTo, bFrom, bTo,
    ),
  });

  return (
    <StaffShell context="brand">
      <div className="flex items-center gap-2 mb-3" data-html2canvas-ignore="true">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => nav("/staff/brand/cancellations")}
          className="gap-1"
          data-testid="brand-cancellation-detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
      </div>
      <div ref={downloadRef} className="bg-background">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">
            Cancellation {branchName ? `· ${branchName}` : ""}
          </h1>
          <p
            className="text-xs font-medium text-sky-600 mt-1"
            data-testid="brand-cancellation-detail-date-label"
          >
            Period A: {formatDate(aFrom)} → {formatDate(aTo)}
            &nbsp;vs&nbsp;
            Period B: {formatDate(bFrom)} → {formatDate(bTo)}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={downloadPng}
          disabled={capturing || loading}
          className="gap-1 h-9 shrink-0"
          data-testid="brand-cancellation-detail-download"
          data-html2canvas-ignore="true"
        >
          <Download className={`w-4 h-4 ${capturing ? "animate-pulse" : ""}`} />
          {capturing ? "Capturing…" : "Download"}
        </Button>
      </div>

      {!canSeeCancellation && (
        <Card className="rounded-lg mb-6">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            You don't have permission to view this dashboard.
          </CardContent>
        </Card>
      )}

      {canSeeCancellation && accessError && (
        <Card className="rounded-lg mb-6" data-testid="brand-cancellation-detail-forbidden">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            {accessError}
          </CardContent>
        </Card>
      )}

      {canSeeCancellation && !accessError && (
        <>
          <Card className="rounded-lg mb-4" data-testid="brand-cancellation-detail-date-range">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <RangePicker label="Period A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} testIdPrefix="brand-cancellation-detail-a" />
              <span className="h-6 w-px bg-border mx-1" />
              <RangePicker label="Period B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} testIdPrefix="brand-cancellation-detail-b" />
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                disabled={loading}
                className="gap-1 ml-auto"
                data-testid="brand-cancellation-detail-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <TotalCard
              label="CLIENT CANCELLATIONS"
              valueA={data?.client_cancellations_a}
              valueB={data?.client_cancellations_b}
              delta={data?.client_cancellations_delta_pct}
              tone="sky"
              caption="Unique client × date"
              testId="brand-cancellation-detail-clients"
              tooltip={CANCELLATION_TIPS.clients}
            />
            <TotalCard
              label="APPOINTMENT CANCELLATIONS"
              valueA={data?.appointment_cancellations_a}
              valueB={data?.appointment_cancellations_b}
              delta={data?.appointment_cancellations_delta_pct}
              tone="amber"
              caption="Singles + 1 per package"
              testId="brand-cancellation-detail-appointments"
              tooltip={CANCELLATION_TIPS.appointments}
            />
            <TotalCard
              label="PACKAGE CANCELLATIONS"
              valueA={data?.package_cancellations_a}
              valueB={data?.package_cancellations_b}
              delta={data?.package_cancellations_delta_pct}
              tone="rose"
              caption="1 per cancelled package"
              testId="brand-cancellation-detail-packages"
              tooltip={CANCELLATION_TIPS.packages}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Chart 1 — Client Cancellations & No-Shows per day (unique client × date). */}
            <ChartCard title="Client Daily Cancellations & No-Shows · A vs B" testId="chart-daily" height={360} tooltip={CANCELLATION_TIPS.clients}>
              <BarChart
                data={daily}
                margin={{ top: 24, right: 10, left: 0, bottom: 28 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="day_index"
                  interval={0}
                  height={56}
                  tickLine={false}
                  tick={(p) => <DailyDualTick {...p} dataLookup={dailyLookup} />}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <ChartTooltip
                  labelFormatter={(idx) => {
                    const r = dailyLookup[idx] || {};
                    return `Day ${idx} · A ${r.date_a || "—"} · B ${r.date_b || "—"}`;
                  }}
                />
                <Legend verticalAlign="top" align="center" iconType="square" height={24} />
                <Bar dataKey="client_cancelled_a" stackId="a" fill={COLOR_A}           name="A · Cancelled" />
                <Bar dataKey="client_no_show_a"   stackId="a" fill={COLOR_NO_SHOW}     name="A · No-show">
                  <LabelList dataKey="client_total_a" position="top" fontSize={10} formatter={(v) => (v > 0 ? v : "")} />
                </Bar>
                <Bar dataKey="client_cancelled_b" stackId="b" fill={COLOR_B}           name="B · Cancelled" />
                <Bar dataKey="client_no_show_b"   stackId="b" fill={COLOR_B_SECONDARY} name="B · No-show">
                  <LabelList dataKey="client_total_b" position="top" fontSize={10} formatter={(v) => (v > 0 ? v : "")} />
                </Bar>
              </BarChart>
            </ChartCard>

            {/* Chart 2 — Client cancellation status split: one pie per period. */}
            <DualPieCard
              title="Client Cancellation Status Split"
              testId="chart-status-split"
              tooltip={CANCELLATION_TIPS.clients}
              labelA={labelA}
              labelB={labelB}
              dataA={statusDataA}
              dataB={statusDataB}
              donut
            />

            {/* Chart 3 — Cancellations by Reason — A bar + B comparison marker. */}
            <ChartCard title="Appointment Cancellations by Reason · A vs B" testId="chart-reasons" tooltip={CANCELLATION_TIPS.appointments}>
              <BarChart
                layout="vertical"
                data={data?.reasons || []}
                margin={{ top: 10, right: 56, left: 20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="reason" width={140} tick={{ fontSize: 11 }} />
                <ChartTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="count_a" name="Period A" fill={COLOR_A} barSize={14}>
                  <LabelList dataKey="count_a" position="right" fontSize={11} />
                </Bar>
                <Bar dataKey="count_b" name="Period B" fill={COLOR_B} barSize={8}>
                  <LabelList dataKey="count_b" position="right" fontSize={10} />
                </Bar>
              </BarChart>
            </ChartCard>

            {/* Chart 4 — Top 10 Practitioners · A bar + B marker. */}
            <ChartCard title="Top 10 Practitioners · A vs B" testId="chart-practitioners" tooltip={CANCELLATION_TIPS.appointments}>
              <BarChart
                layout="vertical"
                data={data?.top_practitioners || []}
                margin={{ top: 10, right: 56, left: 20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <ChartTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="count_a" name="Period A" fill="#7c3aed" barSize={14}>
                  <LabelList dataKey="count_a" position="right" fontSize={11} />
                </Bar>
                <Bar dataKey="count_b" name="Period B" fill={COLOR_B} barSize={8}>
                  <LabelList dataKey="count_b" position="right" fontSize={10} />
                </Bar>
              </BarChart>
            </ChartCard>

            {/* Chart 5 — Top 10 Services · A bar + B marker. */}
            <ChartCard title="Top 10 Services · A vs B" testId="chart-services" tooltip={CANCELLATION_TIPS.appointments}>
              <BarChart
                layout="vertical"
                data={data?.top_services || []}
                margin={{ top: 10, right: 56, left: 20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="service" width={160} tick={{ fontSize: 11 }} />
                <ChartTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="count_a" name="Period A" fill="#0891b2" barSize={14}>
                  <LabelList dataKey="count_a" position="right" fontSize={11} />
                </Bar>
                <Bar dataKey="count_b" name="Period B" fill={COLOR_B} barSize={8}>
                  <LabelList dataKey="count_b" position="right" fontSize={10} />
                </Bar>
              </BarChart>
            </ChartCard>

            {/* Chart 6 — Top 10 Packages · A bar + B marker. */}
            <ChartCard title="Top 10 Packages · A vs B" testId="chart-packages" tooltip={CANCELLATION_TIPS.packages}>
              <BarChart
                layout="vertical"
                data={data?.top_packages || []}
                margin={{ top: 10, right: 56, left: 20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="package" width={160} tick={{ fontSize: 11 }} />
                <ChartTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="count_a" name="Period A" fill="#ca8a04" barSize={14}>
                  <LabelList dataKey="count_a" position="right" fontSize={11} />
                </Bar>
                <Bar dataKey="count_b" name="Period B" fill={COLOR_B} barSize={8}>
                  <LabelList dataKey="count_b" position="right" fontSize={10} />
                </Bar>
              </BarChart>
            </ChartCard>

            {/* Chart 7 — By Hour of Day · A & B as grouped bars. */}
            <ChartCard title="By Hour of Day · A vs B" testId="chart-by-hour" tooltip={CANCELLATION_TIPS.appointments}>
              <BarChart data={data?.by_hour || []} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="hour"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(h) => `${String(h).padStart(2, "0")}:00`}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <ChartTooltip labelFormatter={(h) => `${String(h).padStart(2, "0")}:00`} />
                <Legend verticalAlign="top" align="center" iconType="square" height={24} />
                <Bar dataKey="a" name="Period A" fill="#16a34a">
                  <LabelList dataKey="a" position="top" fontSize={10} formatter={(v) => (v > 0 ? v : "")} />
                </Bar>
                <Bar dataKey="b" name="Period B" fill={COLOR_B}>
                  <LabelList dataKey="b" position="top" fontSize={10} formatter={(v) => (v > 0 ? v : "")} />
                </Bar>
              </BarChart>
            </ChartCard>

            {/* Chart 8 — By Weekday: one pie per period. */}
            <DualPieCard
              title="Appointment Cancellations by Weekday"
              testId="chart-by-weekday"
              tooltip={CANCELLATION_TIPS.appointments}
              labelA={labelA}
              labelB={labelB}
              dataA={weekdayDataA}
              dataB={weekdayDataB}
            />

            {/* Chart 9 — Top 15 Clients, spans both columns. */}
            <div className="lg:col-span-2">
              <ChartCard title="Top 15 Clients by Cancellations · A vs B" testId="chart-top-clients" height={440} tooltip={CANCELLATION_TIPS.clients}>
                <BarChart
                  layout="vertical"
                  data={data?.top_clients || []}
                  margin={{ top: 10, right: 56, left: 20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="customer_name" width={180} tick={{ fontSize: 11 }} />
                  <ChartTooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="count_a" name="Period A" fill="#db2777" barSize={14}>
                    <LabelList dataKey="count_a" position="right" fontSize={11} />
                  </Bar>
                  <Bar dataKey="count_b" name="Period B" fill={COLOR_B} barSize={8}>
                    <LabelList dataKey="count_b" position="right" fontSize={10} />
                  </Bar>
                </BarChart>
              </ChartCard>
            </div>
          </div>
        </>
      )}
      </div>
    </StaffShell>
  );
}

function RangePicker({ label, from, to, onFrom, onTo, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
      <Input
        type="date"
        value={from}
        onChange={(e) => onFrom(e.target.value)}
        className="h-8 w-40 text-xs"
        data-testid={`${testIdPrefix}-from`}
      />
      <span className="text-xs text-muted-foreground">to</span>
      <Input
        type="date"
        value={to}
        onChange={(e) => onTo(e.target.value)}
        className="h-8 w-40 text-xs"
        data-testid={`${testIdPrefix}-to`}
      />
    </div>
  );
}

function TotalCard({ label, valueA, valueB, delta, tone, caption, testId, tooltip }) {
  const toneColor = {
    sky:   "text-sky-600",
    amber: "text-amber-600",
    rose:  "text-rose-600",
  }[tone] || "text-foreground";
  return (
    <Card className="rounded-lg relative" data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-4">
        <p className="tracking-overline text-[10px] text-muted-foreground pr-5">{label}</p>
        <p className={`text-2xl font-semibold mt-1 ${toneColor}`}>
          {(valueA ?? 0).toLocaleString("en-US")}
        </p>
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
          <DeltaBadge delta={delta} testId={`${testId}-delta`} />
          <span className="text-muted-foreground" data-testid={`${testId}-b`}>
            B · {(valueB ?? 0).toLocaleString("en-US")}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">{caption}</p>
      </CardContent>
    </Card>
  );
}
